<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip placement="bottom" content="カテゴリ追加">
        <v-btn icon @click="openCategoryCreateDialog">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </el-tooltip>
      <v-spacer/>
      <toolbar-inline>
        <v-text-field
            v-model="searchText"
            clearable
            placeholder="検索">
        </v-text-field>
      </toolbar-inline>
    </template>

    <el-table
        :data="categoriesView"
        row-key="viewGenre">
      <el-table-column
          prop="viewGenre"
          label="ジャンル名"
          min-width="20">
      </el-table-column>
      <el-table-column
          label="カテゴリ">
        <template #default="{row}">
          <draggable
              class="draggable-container"
              group="genre"
              chosen-class="selected"
              :sort="false"
              @end="handleDragEnd"
              :onChange="handleDragChange"
              :data-genre="row.genre">
            <v-chip
                v-for="category in row.categories"
                :key="category.id"
                :data-category-id="category.id">
              <span class="category-name">{{ category.name }}</span>
              <v-btn icon x-small @click="openCategoryEditDialog(category)">
                <v-icon>mdi-square-edit-outline</v-icon>
              </v-btn>
            </v-chip>
          </draggable>
        </template>
      </el-table-column>
      <el-table-column
          width="120"
          align="right"
          fixed="right">
        <template #default="{row}">
          <icon-btns>
            <template v-if="row.genre">
              <icon-btn
                  type="delete"
                  tooltip="ジャンルを削除"
                  @click="openGenreDeleteDialog(row)">
              </icon-btn>
              <icon-btn
                  type="edit-content"
                  tooltip="ジャンルを編集"
                  @click.stop="openGenreEditDialog(row)">
              </icon-btn>
            </template>
          </icon-btns>
        </template>
      </el-table-column>
    </el-table>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
           ジャンル系ダイアログ
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->

    <!-- ジャンル編集ダイアログ -->
    <simple-dialog
        :value="dialogGenreEdit"
        title-text="ジャンル編集"
        @cancel="closeGenreEditDialog(false)"
        @apply="closeGenreEditDialog(true)">
      <v-row>
        <v-col cols="12">
          <v-text-field
              autofocus
              v-model="dialogGenreEdit.name"
              label="ジャンル名"
              :rules="[v => !!v || 'ジャンル名が必要です']"/>
        </v-col>
      </v-row>
    </simple-dialog>

    <!-- ジャンル削除ダイアログ -->
    <simple-dialog
        :value="dialogGenreDelete"
        title-text="ジャンル削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のジャンルを削除します。本当によろしいですか？"
        @cancel="closeGenreDeleteDialog(false)"
        @apply="closeGenreDeleteDialog(true)">
      <v-row>
        <v-col cols="12">
          <v-text-field
              v-model="dialogGenreDelete.name"
              label="ジャンル名"
              readonly/>
        </v-col>
        <v-col cols="12">
          <el-radio-group v-model="deleteCategoryWithGenre">
            <el-radio :label="true">
              ジャンル内のカテゴリも同時に全て削除する
            </el-radio>
            <el-radio :label="false">
              ジャンル内のカテゴリを「ジャンル指定無し」に移動する
            </el-radio>
          </el-radio-group>
        </v-col>
      </v-row>
    </simple-dialog>


    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
           カテゴリ系ダイアログ
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->

    <!-- カテゴリ作成ダイアログ -->
    <simple-dialog
        :value="dialogCategoryCreate"
        title-text="カテゴリ作成"
        @close="closeCategoryCreateDialog">
      <v-row>
        <v-col cols="12">
          <v-text-field
              autofocus
              v-model="dialogCategoryCreate.name"
              label="カテゴリ名"
              hint="後から変更が可能です"
              :rules="[
                  v => !!v || 'カテゴリ名が必要です',
                  v => !findCategoryByName(v) || '既に同名のカテゴリが存在します'
              ]"/>
        </v-col>
        <v-col cols="12">
          <v-textarea
              auto-grow
              rows="1"
              v-model="dialogCategoryCreate.description"
              label="説明"
              messages="複数行入力可"/>
        </v-col>
        <v-col cols="12">
          <v-custom-combobox
              v-model="dialogCategoryCreate.genre"
              :items="genres"
              label="ジャンル"
              hint="既存のジャンルを選択するか、新規のジャンルを入力してください。ジャンル無しも可能です。"
              persistent-hint/>
        </v-col>
      </v-row>
    </simple-dialog>

    <!-- カテゴリ編集ダイアログ -->
    <simple-dialog
        :value="dialogCategoryEdit"
        title-text="カテゴリ編集"
        @cancel="closeCategoryEditDialog(false)"
        @apply="closeCategoryEditDialog('apply')">
      <template #default>
        <v-row>
          <v-col cols="12">
            <v-text-field
                autofocus
                v-model="dialogCategoryEdit.name"
                label="カテゴリ名"
                :rules="[
                    v => !!v || 'カテゴリ名が必要です',
                    v => !findCategoryByName(v) || '既に同名のカテゴリが存在します'
                ]">
            </v-text-field>
          </v-col>
          <v-col cols="12">
            <v-textarea
                auto-grow
                rows="1"
                v-model="dialogCategoryEdit.description"
                label="説明"
                messages="複数行入力可">
            </v-textarea>
          </v-col>
          <v-col cols="12">
            <v-custom-combobox
                v-model="dialogCategoryEdit.genre"
                :items="genres"
                clearable
                label="ジャンル"
                hint="既存のジャンルを選択するか、新規のジャンルを入力してください。ジャンル無しも可能です。"
                persistent-hint>
            </v-custom-combobox>
          </v-col>
        </v-row>
      </template>
      <template #actions>
        <v-row align="center" dense>
          <v-col>
            <v-btn
                color="delete"
                @click="closeCategoryEditDialog('delete')"
                large
                dark
                text>
              削除
            </v-btn>
          </v-col>
        </v-row>
      </template>
    </simple-dialog>

    <!-- カテゴリ削除ダイアログ -->
    <simple-dialog
        :value="dialogCategoryDelete"
        title-text="カテゴリ削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のカテゴリを削除します。本当によろしいですか？"
        @cancel="closeCategoryDeleteDialog(false)"
        @apply="closeCategoryDeleteDialog(true)">
      <template #default>
        <v-row>
          <v-col cols="12">
            <text-field-like
                v-model="dialogCategoryDelete.name"
                label="カテゴリ名">
            </text-field-like>
          </v-col>
          <v-col cols="12">
            <text-field-like
                v-model="dialogCategoryDelete.description"
                label="説明"
                multi-line>
            </text-field-like>
          </v-col>
          <v-col cols="12">
            <v-custom-combobox
                v-model="dialogCategoryDelete.genre"
                :items="genres"
                label="ジャンル"
                readonly>
            </v-custom-combobox>
          </v-col>
        </v-row>
      </template>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import Vue from "vue";
import {VCombobox} from "vuetify/lib";
import natsort from "natsort";
import draggable from "vuedraggable";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import {generateTemporaryID} from "@/store/modules/hds";
import {mapGetters, mapMutations} from "vuex";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";

const sortFn = natsort();
const VCustomCombobox = VCombobox.extend({
  created() {
    this.$on('blur', () => {
      // フォーカスロストと同時に input イベントを emit させる
      this.updateCombobox();

      // フォーカスロスト後に余計なイベントを emit させない
      this.isFocused = false;
      this.isMenuActive = false;
    })
  }
});

export default {
  components: {
    TextFieldLike,
    IconBtn, IconBtns, TabItemTemplate, SimpleDialog, draggable, VCustomCombobox, ToolbarInline},
  props: {
    dictionary: Object
  },
  data() {
    return {
      searchText: null,
      dialogCategoryCreate: false,
      dialogCategoryEdit: false,
      dialogCategoryDelete: false,
      dialogGenreEdit: false,
      dialogGenreDelete: false,
      deleteCategoryWithGenre: true,
    };
  },
  computed: {
    ...mapGetters({
      entities: 'hds/entities'
    }),
    categoriesView() {
      if (!this.searchText) {
        return this.categories;
      }
      return this.categories.filter(row =>
          row.genre?.indexOf(this.searchText) !== -1 ||
          row.categories.find(c => c.name.indexOf(this.searchText) !== -1)
      );
    },
    categories() {
      const _e = this.entities;
      const grouped = new Map();
      const categories = _e.category.find();
      for (let c of categories) {
        const genre = c.genre || null;
        if (grouped.has(genre)) {
          grouped.get(genre).push(c);
        } else {
          grouped.set(genre, [c]);
        }
      }

      const result = Array.from(grouped.entries())
          .map(([genre, categories]) => {
            return {
              genre,
              viewGenre: genre || 'ジャンル指定無し',
              categories: categories.sort((a, b) => sortFn(a.name, b.name))
            };
          })
          .sort((a, b) => sortFn(a.genre, b.genre));
      return Vue.observable(result);
    },
    genres() {
      const result = new Set();
      const _e = this.entities;
      _e.category.find().forEach(c => {
        if (c.genre) {
          result.add(c.genre);
        }
      });
      return Array.from(result);
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),

    /**
     * カテゴリ名からカテゴリを取得します
     *
     * @param categoryName カテゴリ名
     */
    findCategoryByName(categoryName) {
      const _e = this.entities;
      return _e.category.findOne({name: categoryName});
    },

    /**
     * DnD で移動した際の v-chip を正しい位置に移動する
     */
    handleDragChange(e) {
      const _e = this.entities;
      const to = e.to;
      if (!to) {
        return;
      }
      const choosen = to.querySelector('.selected');
      if (!choosen) {
        return;
      }
      const choosenCategoryId = choosen.dataset.categoryId;
      const categories = Array.from(to.querySelectorAll('[data-category-id]'))
          .map(el => {
            return {
              el,
              category: _e.category.findOne({id: el.dataset.categoryId})
            };
          })
          .sort((a, b) => sortFn(a.category.name, b.category.name));
      const index = categories
          .map(c => c.category.id)
          .lastIndexOf(choosenCategoryId, -2);
      if (index === -1) {
        to.appendChild(choosen);
      } else {
        to.insertBefore(choosen, categories[index + 1].el);
      }
    },
    /**
     * DnD の移動が確定した際に、データに書き込む
     */
    handleDragEnd(e) {
      const _e = this.entities;
      const to = e.to;
      if (!to) {
        return;
      }
      const category = _e.category.findOne({
        id: e.item.dataset.categoryId
      });
      const genre = to.dataset.genre || null;
      if (genre === category.genre) {
        return;
      } else {
        category.genre = genre;
      }
      _e.category.update(category);
      if(e.item) { // 後でリアクティブに更新されるため、移動したアイテムを削除
        e.item.remove();
      }
      this.$emit('dirty');
    },

    ///////////////////////////////////////////////////////////////////////////////////////
    // ジャンル系ダイアログ
    ///////////////////////////////////////////////////////////////////////////////////////

    openGenreEditDialog(row) {
      this.dialogGenreEdit = {name: row.genre, row};
    },
    closeGenreEditDialog(apply) {
      const _e = this.entities;
      if (apply) {
        const {row, name} = this.dialogGenreEdit;
        row.genre = name;
        row.categories.forEach(category => {
          category.genre = name;
          _e.category.update(category);
        });
        this.$emit('dirty');
      }
      this.dialogGenreEdit = false;
    },
    openGenreDeleteDialog(row) {
      this.dialogGenreDelete = {name: row.genre, row};
    },
    closeGenreDeleteDialog(apply) {
      const _e = this.entities;
      if (apply) {
        const {row, name} = this.dialogGenreDelete;
        if (this.deleteCategoryWithGenre) {
          _e.category.findAndRemove({genre: name});
        } else {
          _e.category
              .find({genre: name})
              .forEach(c => {
                c.genre = null;
                _e.category.update(c);
              });
        }
        this.$emit('dirty');
        this.showSnackbar({
          message: `ジャンル「${this.dialogGenreDelete.name}」が削除されました`
        });
      }
      this.dialogGenreDelete = false;
    },

    ///////////////////////////////////////////////////////////////////////////////////////
    // カテゴリ系ダイアログ
    ///////////////////////////////////////////////////////////////////////////////////////

    openCategoryCreateDialog() {
      this.dialogCategoryCreate = {
        id: generateTemporaryID(),
        dictionaryId: this.dictionary.id,
        name: '',
        description: '',
        genre: null
      };
    },
    closeCategoryCreateDialog(apply) {
      const _e = this.entities;
      if (apply) {
        _e.category.insert(this.dialogCategoryCreate);
        this.$emit('dirty');
      }
      this.dialogCategoryCreate = false;
    },
    openCategoryEditDialog(category) {
      this.dialogCategoryEdit = Object.assign({original: category}, category);
    },
    closeCategoryEditDialog(apply) {
      const _e = this.entities;
      if (apply === 'apply') {
        // オリジナルのインスタンスに反映して画面を更新する
        const original = this.dialogCategoryEdit.original;
        delete this.dialogCategoryEdit.original;
        Object.assign(original, this.dialogCategoryEdit);

        // データを更新
        _e.category.update(original);
        this.$emit('dirty');
      } else if (apply === 'delete') {
        this.dialogCategoryDelete = this.dialogCategoryEdit;
      }
      this.$nextTick(() => {
        this.dialogCategoryEdit = false;
      });
    },
    closeCategoryDeleteDialog(apply) {
      const _e = this.entities;
      if (apply) {
        _e.category.remove(this.dialogCategoryDelete);
        this.$emit('dirty');
        this.$store.commit({
          type: 'showSnackbar',
          message: `カテゴリ「${this.dialogCategoryDelete.name}」が削除されました`
        });
      }
      this.dialogCategoryDelete = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.draggable-container {
  min-height: 32px;
}

.category-name {
  margin-right: 5px;
}

.description {
  white-space: pre-wrap;
}
</style>
